import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createDMChannel,
  deleteMessage,
  editMessage,
  fetchChannels,
  fetchMessages,
  markChannelRead,
  muteChannel,
  sendMessage,
  type ChannelWithMeta,
  type MessageInfo,
} from '../services/messaging';

export const messagingKeys = {
  root: ['messaging'] as const,
  channels: () => [...messagingKeys.root, 'channels'] as const,
  messages: (channelId: string) =>
    [...messagingKeys.root, 'messages', channelId] as const,
};

export const useChannels = () =>
  useQuery<{ channels: ChannelWithMeta[] }>(
    messagingKeys.channels(),
    fetchChannels,
    { retry: false },
  );

export const useMessages = (channelId: string | null) =>
  useQuery<{ messages: MessageInfo[]; has_more: boolean }>(
    messagingKeys.messages(channelId ?? ''),
    () => fetchMessages(channelId!),
    { enabled: !!channelId, retry: false, refetchInterval: 3000 },
  );

export const useSendMessage = () => {
  const client = useQueryClient();
  return useMutation(
    ({ channelId, ...data }: { channelId: string; content: string; content_type?: string; reply_to?: string }) =>
      sendMessage(channelId, data),
    {
      onSuccess: (_data, vars) => {
        client.invalidateQueries(messagingKeys.messages(vars.channelId));
        client.invalidateQueries(messagingKeys.channels());
      },
    },
  );
};

export const useEditMessage = () => {
  const client = useQueryClient();
  return useMutation(
    ({ channelId, messageId, content }: { channelId: string; messageId: string; content: string }) =>
      editMessage(channelId, messageId, { content }),
    {
      onSuccess: (_data, vars) => {
        client.invalidateQueries(messagingKeys.messages(vars.channelId));
      },
    },
  );
};

export const useDeleteMessage = () => {
  const client = useQueryClient();
  return useMutation(
    ({ channelId, messageId }: { channelId: string; messageId: string }) =>
      deleteMessage(channelId, messageId),
    {
      onSuccess: (_data, vars) => {
        client.invalidateQueries(messagingKeys.messages(vars.channelId));
      },
    },
  );
};

export const useMarkRead = () => {
  const client = useQueryClient();
  return useMutation(
    (channelId: string) => markChannelRead(channelId),
    {
      onSuccess: () => {
        client.invalidateQueries(messagingKeys.channels());
      },
    },
  );
};

export const useMuteChannel = () => {
  const client = useQueryClient();
  return useMutation(
    ({ channelId, muted }: { channelId: string; muted: boolean }) =>
      muteChannel(channelId, muted),
    {
      onSuccess: () => {
        client.invalidateQueries(messagingKeys.channels());
      },
    },
  );
};

export const useCreateDM = () => {
  const client = useQueryClient();
  return useMutation(createDMChannel, {
    onSuccess: () => {
      client.invalidateQueries(messagingKeys.channels());
    },
  });
};
